import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useRefetchOnFocus } from '../hooks/useRefetchOnFocus';
import { EmptyState } from '../components/ui/EmptyState';
import { PageHeader } from '../components/ui/PageHeader';
import type { DaedalusRunnerHeartbeat, DaedalusRunnerStatus } from '@pantheon/shared';

interface RunnerLogLine {
  t: string;
  s: string;
  m: string;
}

/** Live console for one runner — polls the rolling log every 4s while open */
function RunnerLog({ runnerId }: { runnerId: string }) {
  const { get } = useApi();
  const [lines, setLines] = useState<RunnerLogLine[]>([]);
  const [loading, setLoading] = useState(true);
  const getRef = useRef(get);
  getRef.current = get;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pinnedToBottom = useRef(true);

  useEffect(() => {
    const fetchLog = async () => {
      const result = await getRef.current<{ lines: RunnerLogLine[] }>(
        `/daedalus/runners/${encodeURIComponent(runnerId)}/log`,
      );
      if (result.success && result.data) setLines(result.data.lines ?? []);
      setLoading(false);
    };
    fetchLog();
    const interval = setInterval(fetchLog, 4000);
    return () => clearInterval(interval);
  }, [runnerId]);

  // Auto-scroll to the newest line unless the user scrolled up
  useEffect(() => {
    const el = scrollRef.current;
    if (el && pinnedToBottom.current) el.scrollTop = el.scrollHeight;
  }, [lines]);

  return (
    <div
      ref={scrollRef}
      onScroll={(e) => {
        const el = e.currentTarget;
        pinnedToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
      }}
      className="mt-3 bg-gray-900 rounded-lg p-3 max-h-80 overflow-y-auto font-mono text-xs leading-relaxed"
    >
      {loading ? (
        <p className="text-gray-500">Loading log...</p>
      ) : lines.length === 0 ? (
        <p className="text-gray-500">No activity logged yet.</p>
      ) : (
        lines.map((line, i) => (
          <div key={i} className="whitespace-pre-wrap break-words">
            <span className="text-gray-500">{new Date(line.t).toLocaleTimeString()} </span>
            <span className={line.s === 'runner' ? 'text-purple-400' : 'text-cyan-400'}>[{line.s}]</span>{' '}
            <span className={line.m.startsWith('→') ? 'text-amber-300' : 'text-gray-200'}>{line.m}</span>
          </div>
        ))
      )}
    </div>
  );
}

function isOnline(runner: DaedalusRunnerHeartbeat): boolean {
  const interval = runner.pollIntervalMs ?? 60_000;
  return Date.now() - new Date(runner.lastSeenAt).getTime() < interval * 2 + 15_000;
}

function timeAgo(iso: string): string {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const statusStyle: Record<string, { dot: string; label: string; color: string }> = {
  idle: { dot: 'bg-green-500', label: 'Online — idle', color: 'text-green-700' },
  working: { dot: 'bg-blue-500 animate-pulse', label: 'Working', color: 'text-blue-700' },
  rate_limited: { dot: 'bg-amber-500', label: 'Rate limited', color: 'text-amber-700' },
  offline: { dot: 'bg-red-500', label: 'Offline', color: 'text-red-600' },
};

export function RunnersPage() {
  const { get } = useApi();
  const [status, setStatus] = useState<DaedalusRunnerStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedRunner, setExpandedRunner] = useState<string | null>(null);
  const getRef = useRef(get);
  getRef.current = get;

  // Stable fetch (no loading spinner) reused by the poll interval and the focus refetch.
  const fetchStatus = useCallback(async () => {
    const result = await getRef.current<DaedalusRunnerStatus>('/daedalus/runner-status');
    if (result.success && result.data) setStatus(result.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 15_000);
    return () => clearInterval(interval);
  }, [fetchStatus]);
  useRefetchOnFocus(fetchStatus);

  const runners = [...(status?.runners ?? [])].sort((a, b) => a.runnerId.localeCompare(b.runnerId));

  // A runner is "stale" when its build commit is older than the newest in the fleet — another
  // machine has already pulled + rebuilt a newer version and this one needs a restart to catch up.
  const latestCommitAt = runners.reduce<string | null>(
    (max, r) => (r.commitAt && (!max || new Date(r.commitAt) > new Date(max)) ? r.commitAt : max),
    null,
  );
  const isStale = (r: DaedalusRunnerHeartbeat): boolean =>
    !!r.commitAt && !!latestCommitAt && new Date(r.commitAt).getTime() < new Date(latestCommitAt).getTime();
  const staleCount = runners.filter(isStale).length;

  return (
    <div className="max-w-4xl animate-rise">
      <PageHeader
        mark="R"
        markBg="bg-primary-light"
        markText="text-primary"
        title="Runners"
        subtitle="Local Daedalus runners on your computers. Runners offline for 24 hours disappear automatically."
      />

      {status && status.runnerMode !== 'local' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5 text-sm text-amber-800">
          Runner Mode is set to <strong>Cloud (Fargate)</strong> — local runners idle until you switch to Local
          Runner in Settings → Daedalus.
        </div>
      )}

      {staleCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5 text-sm text-amber-800">
          <strong>{staleCount} runner{staleCount > 1 ? 's are' : ' is'} running older code.</strong>{' '}
          A newer version is live elsewhere in the fleet. Press <span className="font-mono">[Q]</span> in each
          flagged runner's window and relaunch <span className="font-mono">start-runner.cmd</span> to pull and rebuild.
        </div>
      )}

      {loading ? (
        <p className="text-gray-500 text-sm">Loading runners...</p>
      ) : runners.length === 0 ? (
        <EmptyState
          title="No runners have connected yet"
          hint="Start one with node dist/index.js in agents/daedalus-runner — it registers itself here."
        />
      ) : (
        <div className="space-y-3">
          {runners.map((runner) => {
            const online = isOnline(runner);
            const style = statusStyle[online ? runner.status : 'offline'] ?? statusStyle.offline;
            const capacity = runner.capacity ?? 1;
            const active = runner.activeSessions ?? [];

            const isExpanded = expandedRunner === runner.runnerId;
            return (
              <div key={runner.runnerId} className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 p-5">
                <div
                  className="flex items-center justify-between mb-3 cursor-pointer"
                  onClick={() => setExpandedRunner(isExpanded ? null : runner.runnerId)}
                  title={isExpanded ? 'Hide live log' : 'Show live log'}
                >
                  <div className="flex items-center gap-3">
                    <svg
                      className={`w-4 h-4 text-gray-500 shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                    <span className={`w-2.5 h-2.5 rounded-full ${style.dot}`} />
                    <span className="font-semibold text-gray-900">{runner.runnerId}</span>
                    {runner.host && runner.host !== runner.runnerId && (
                      <span className="text-xs text-gray-500">({runner.host})</span>
                    )}
                    <span className={`text-sm font-medium ${style.color}`}>
                      {style.label}
                      {online && runner.status === 'rate_limited' && runner.rateLimitedUntil && (
                        <> — resumes {new Date(runner.rateLimitedUntil).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</>
                      )}
                    </span>
                    {online && runner.needsLogin && (
                      <span
                        className="text-xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-800 border border-red-200"
                        title={`${runner.needsLogin.reason} — since ${new Date(runner.needsLogin.since).toLocaleString()}. Run \`claude\` on this machine and /login; it resumes claiming work on its own.`}
                      >
                        🔑 needs Claude login
                      </span>
                    )}
                    {isStale(runner) && (
                      <span
                        className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200"
                        title="On an older commit than the rest of the fleet — press [Q] and relaunch start-runner.cmd to update."
                      >
                        ⟳ update available
                      </span>
                    )}
                    {runner.engine && (
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full border ${
                          runner.engine === 'pty'
                            ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                            : 'bg-slate-100 text-slate-700 border-slate-200'
                        }`}
                        title={runner.engine === 'pty'
                          ? 'Interactive Claude Code terminal (node-pty) — runs coding sessions on the flat-rate subscription'
                          : 'Headless claude -p (print engine)'}
                      >
                        {runner.engine === 'pty' ? 'PTY engine' : 'claude -p'}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 text-right">
                    <p>Last seen {timeAgo(runner.lastSeenAt)}</p>
                    {runner.startedAt && <p>Started {timeAgo(runner.startedAt)}</p>}
                    {typeof runner.busyMsToday === 'number' && (
                      <p title="Busy time today (UTC) — new work rotates toward the least-used runner">
                        Busy today {(runner.busyMsToday / 3_600_000).toFixed(1)}h
                        {runner.lastClaimAt && <> · last claim {timeAgo(runner.lastClaimAt)}</>}
                      </p>
                    )}
                    {runner.commit && (
                      <p className={isStale(runner) ? 'text-amber-600 font-mono' : 'font-mono text-gray-500'}>{runner.commit}</p>
                    )}
                  </div>
                </div>

                {/* Session slots + planning work */}
                <div className="flex gap-2 flex-wrap">
                  {runner.planningJob && online && (
                    <div className="px-3 py-2 rounded-lg bg-purple-50 border border-purple-200 text-xs">
                      <p className="font-mono text-purple-700">{runner.planningJob.kind}</p>
                      <p className="text-purple-500 mt-0.5">planning</p>
                    </div>
                  )}
                  {(() => {
                    // Claimed-but-not-executing sessions (paused on a rate limit,
                    // or between resume ticks) fill slots so they don't read as empty
                    const paused = (runner.claimedSessions ?? []).filter(
                      (s) => !active.some((a) => a.sessionId === s.sessionId),
                    );
                    const resumeAt = runner.rateLimitedUntil
                      ? new Date(runner.rateLimitedUntil).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                      : null;
                    return Array.from({ length: Math.max(capacity, active.length + paused.length) }, (_, i) => {
                      const session = active[i];
                      if (session) {
                        return (
                          <div key={i} className="px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-xs">
                            <p className="font-mono text-blue-700">{session.branch ?? session.sessionId.slice(0, 8)}</p>
                            <p className="text-blue-500 mt-0.5">running</p>
                          </div>
                        );
                      }
                      const pausedSession = paused[i - active.length];
                      if (pausedSession && online) {
                        return (
                          <div key={i} className="px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-xs">
                            <p className="font-mono text-amber-700">{pausedSession.branch ?? pausedSession.sessionId.slice(0, 8)}</p>
                            <p className="text-amber-600 mt-0.5">
                              {runner.status === 'rate_limited'
                                ? `paused — resumes ${resumeAt ?? 'when the limit resets'}`
                                : 'paused — resuming'}
                            </p>
                          </div>
                        );
                      }
                      return (
                        <div key={i} className="px-3 py-2 rounded-lg bg-gray-50 border border-dashed border-gray-200 text-xs text-gray-500">
                          empty slot
                        </div>
                      );
                    });
                  })()}
                </div>

                {/* Live console */}
                {isExpanded && <RunnerLog runnerId={runner.runnerId} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
